import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { loadStorePage } from "../store/actions/storeActions";
import { cPath } from "../constants";
import { Container } from "../components/layout/Container";
import StoreHeroSection from "../components/store-detail/StoreHeroSection";
import StoreBreadcrumbBar from "../components/store-detail/StoreBreadcrumbBar";
import StoreCouponsColumn from "../components/store-detail/StoreCouponsColumn";
import StoreSideColumn from "../components/store-detail/StoreSideColumn";
import StoreSeoContent from "../components/store-detail/StoreSeoContent";
import NewsletterBanner from "../components/shared/NewsletterBanner";
import CrawlerSeoContent from "../components/seo/CrawlerSeoContent";
import useSEO from "../hooks/useSEO";
import seoConfig from "../config/seoConfig";
import { getLocalizedStore } from "../utils/getLocalizedStore";
import {
  generateStoreSchema,
  generateBreadcrumbSchema,
  generateFAQSchema,
} from "../utils/schemaGenerators";

const NotFound = styled.div`
  text-align: center;
  padding: 5rem 1rem;
`;

const Body = styled.div`
  padding: 2rem 0;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const MainCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const SideCol = styled.div`
  width: 100%;
  @media (min-width: 1024px) {
    width: 20rem;
    flex-shrink: 0;
  }
`;

const LoadNote = styled.p`
  text-align: center;
  padding: 2rem 1rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const Freshness = styled.p`
  margin: 0 0 1rem;
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

export default function StorePage() {
  const dispatch = useDispatch();
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const s = slug || "";

  const {
    store: displayStore,
    storeCoupons: displayCoupons,
    stores: apiStores,
    loading,
    error,
  } = useSelector((state) => state.store);

  useEffect(() => {
    dispatch(loadStorePage(s));
  }, [s, dispatch]);

  const localized = useMemo(
    () => getLocalizedStore(displayStore, i18n.language),
    [displayStore, i18n.language]
  );

  const relatedStores = useMemo(() => {
    if (!displayStore) return [];
    return (apiStores || [])
      .filter((x) => x.category === displayStore.category && x.id !== displayStore.id)
      .slice(0, 6);
  }, [displayStore, apiStores]);

  const activeFaqs = useMemo(
    () => (localized.faqs || []).filter((f) => f.isActive !== false),
    [localized.faqs]
  );

  const seo = useSEO({
    title:
      localized.seo?.title ||
      (displayStore ? `${displayStore.name} Coupons & Deals` : undefined),
    description:
      localized.seo?.description ||
      (displayStore
        ? `Save at ${displayStore.name} with verified promo codes and exclusive deals.`
        : undefined),
    keywords: localized.seo?.keywords
      ? String(localized.seo.keywords)
          .split(/[,;]+/)
          .map((k) => k.trim())
          .filter(Boolean)
      : undefined,
    canonical: localized.seo?.canonicalUrl || undefined,
    image: localized.seo?.ogImage || displayStore?.logo,
    noindex: localized.seo?.robots?.includes("noindex"),
    nofollow: localized.seo?.robots?.includes("nofollow"),
    schema: displayStore
      ? [
          generateStoreSchema({
            ...displayStore,
            description: localized.description || localized.seo?.intro || "",
          }),
          generateBreadcrumbSchema([
            { name: "Home", url: seoConfig.siteUrl },
            { name: "Stores", url: `${seoConfig.siteUrl}/stores` },
            {
              name: localized.pageTitle || displayStore.name,
              url: `${seoConfig.siteUrl}/store/${displayStore.slug || s}`,
            },
          ]),
          activeFaqs.length ? generateFAQSchema(activeFaqs) : null,
        ].filter(Boolean)
      : undefined,
  });

  if (loading) {
    return (
      <>
        {seo}
        <LoadNote>Loading store…</LoadNote>
      </>
    );
  }

  if (!displayStore) {
    return (
      <>
        {seo}
        <NotFound>
          <h1 style={{ marginBottom: "1rem" }}>Store not found</h1>
          {error && <p style={{ color: "hsl(220 10% 46%)" }}>{error}</p>}
          <Link to={cPath("stores")}>← Browse all stores</Link>
        </NotFound>
      </>
    );
  }

  const coupons = displayCoupons || [];

  const highlights = {
    bestDiscount: displayStore.bestDiscount || "—",
    couponCodes: coupons.filter((c) => c.type === "code").length,
    totalOffers: coupons.length,
    avgSavings: "—",
  };

  const storeForHero = {
    ...displayStore,
    name: localized.name || localized.pageTitle || displayStore.name,
    logoAlt: localized.logoAlt,
  };

  return (
    <>
      {seo}
      <StoreBreadcrumbBar store={displayStore} />
      <CrawlerSeoContent
        title={localized.seo?.h1 || localized.pageTitle || displayStore?.name}
        intro={localized.seo?.intro || localized.description}
        faqs={activeFaqs}
      />
      <StoreHeroSection store={storeForHero} highlights={highlights} />
      <Body>
        <Container>
          {(displayStore.lastVerifiedAt || displayStore.contentUpdatedAt) && (
            <Freshness>
              {displayStore.lastVerifiedAt &&
                `Last verified ${new Date(displayStore.lastVerifiedAt).toLocaleDateString(i18n.language)}`}
              {displayStore.lastVerifiedAt && displayStore.contentUpdatedAt && " · "}
              {displayStore.contentUpdatedAt &&
                `Updated ${new Date(displayStore.contentUpdatedAt).toLocaleDateString(i18n.language)}`}
            </Freshness>
          )}
          <Grid>
            <MainCol>
              <StoreCouponsColumn store={displayStore} coupons={coupons} />
              <StoreSeoContent store={displayStore} localized={localized} />
            </MainCol>
            <SideCol>
              <StoreSideColumn
                store={displayStore}
                highlights={highlights}
                relatedStores={relatedStores}
                localized={localized}
              />
            </SideCol>
          </Grid>
        </Container>
      </Body>
      <NewsletterBanner />
    </>
  );
}
